import { DependencyList, RefObject, useCallback, useEffect, useState } from "react";
import { extractTocItems, TocItem, TOC_SELECTOR } from "./tocUtils";

/**
 * AI 검토안 본문 컨테이너에서 마크다운 헤더 목차를 수집하는 훅.
 *
 * 본문은 react-markdown으로 동기 렌더되지만, deps(원본/수정본 토글·편집모드·리뷰 변경)
 * 직후 DOM이 갱신된 시점을 잡기 위해 requestAnimationFrame으로 한 프레임 뒤에 수집한다.
 *
 * @param containerRef 헤더를 포함하는 스크롤 컨테이너 ref
 * @param deps 콘텐츠/모드가 바뀌어 재수집이 필요한 의존성 목록
 */
export const useReviewToc = (
  containerRef: RefObject<HTMLElement>,
  deps: DependencyList
) => {
  const [items, setItems] = useState<TocItem[]>([]);

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      setItems(extractTocItems(containerRef.current));
    });
    return () => window.cancelAnimationFrame(rafId);
    // containerRef는 안정적이므로 deps에서 제외. 재수집은 호출부가 넘긴 deps로 제어.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const scrollToItem = useCallback(
    (item: TocItem) => {
      const container = containerRef.current;
      if (!container) return;

      // 클릭 시점에 현재 DOM에서 같은 위치의 노드를 다시 찾아 stale element를 피한다.
      // (item.id는 querySelectorAll 결과 인덱스라 DOM이 같으면 동일 노드를 가리킨다)
      const nodes = container.querySelectorAll<HTMLElement>(TOC_SELECTOR);
      const el = nodes[item.id] ?? item.element;
      if (!el || !el.isConnected) return;

      // 스크롤 컨테이너(ReviewContentContainer, overflow-y:auto)를 직접 스크롤한다.
      // 중첩 스크롤 환경에서 scrollIntoView가 엉뚱한 조상을 스크롤하는 문제를 피한다.
      const top =
        container.scrollTop +
        el.getBoundingClientRect().top -
        container.getBoundingClientRect().top;
      container.scrollTo({ top, behavior: "smooth" });
    },
    [containerRef]
  );

  return { items, scrollToItem };
};
